import { useState, useEffect, useRef } from 'react';

// Smoothing factor (Low-pass filter)
// 0.1 means 10% new value + 90% old value
// Higher value = Faster response, more jitter
// Lower value = Slower response, smoother
const SMOOTHING_FACTOR = 0.15;

/**
 * 🧭 useCompassHeading - Robust Compass Heading Hook
 * 
 * Handles:
 * 1. iOS (webkitCompassHeading)
 * 2. Android (deviceorientationabsolute)
 * 3. Screen Orientation Compensation
 * 4. Smoothing to reduce jitter
 * 
 * @returns {number} heading (0-360 degrees, 0 = North)
 */
export const useCompassHeading = () => {
    const [heading, setHeading] = useState(0);
    const prevHeadingRef = useRef(0);

    useEffect(() => {
        // 브라우저/임베드 정책에서 센서가 차단된 경우 이벤트 리스너 자체를 등록하지 않는다.
        const policy = document.permissionsPolicy || document.featurePolicy;
        if (policy && typeof policy.allowsFeature === 'function') {
            const accelerometerAllowed = policy.allowsFeature('accelerometer');
            const gyroscopeAllowed = policy.allowsFeature('gyroscope');
            const magnetometerAllowed = policy.allowsFeature('magnetometer');
            if (!accelerometerAllowed && !gyroscopeAllowed && !magnetometerAllowed) {
                return undefined;
            }
        }

        if (
            typeof window === 'undefined' ||
            (typeof window.DeviceOrientationEvent === 'undefined' &&
                !('ondeviceorientation' in window) &&
                !('ondeviceorientationabsolute' in window))
        ) {
            return undefined;
        }

        const handleOrientation = (event) => {
            let newHeading = 0;

            // 1. Get Absolute Heading (0 = North)
            if (event.webkitCompassHeading) {
                // iOS
                newHeading = event.webkitCompassHeading;
            } else if (event.absolute && event.alpha !== null) {
                // Android (Modern)
                newHeading = 360 - event.alpha;
            } else if (event.alpha !== null) {
                // Android (Fallback - Relative logic, usually inaccurate for absolute north)
                // But better than nothing.
                newHeading = 360 - event.alpha;
            } else {
                return; // No valid data
            }

            // 2. Compensate for Screen Orientation
            // If phone is rotated (Landscape), compass value is offset.
            let screenAngle = 0;
            if (window.screen?.orientation?.angle) {
                screenAngle = window.screen.orientation.angle;
            } else if (window.orientation) {
                screenAngle = window.orientation;
            }

            newHeading -= screenAngle;

            // Normalize to 0-360
            newHeading = (newHeading + 360) % 360;

            // 3. Smoothing (Linear Interpolation for circular values)
            let prev = prevHeadingRef.current;
            let diff = newHeading - prev;

            // Shortest path interpolation (e.g. 350 -> 10 should be +20, not -340)
            if (diff > 180) diff -= 360;
            if (diff < -180) diff += 360;

            // Apply low-pass filter
            if (Math.abs(diff) < 1) {
                // Ignore micro changes to prevent jitter
                return;
            }

            const smoothedHeading = (prev + diff * SMOOTHING_FACTOR + 360) % 360;

            prevHeadingRef.current = smoothedHeading;
            setHeading(smoothedHeading);
        };

        // Standard event
        const supportsAbsolute = 'ondeviceorientationabsolute' in window;

        if (supportsAbsolute) {
            window.addEventListener('deviceorientationabsolute', handleOrientation, true);
        } else {
            window.addEventListener('deviceorientation', handleOrientation, true);
        }

        // Handle screen rotation dynamically
        // (Just in case user rotates phone while using compass)
        // No explicit listener needed for 'orientationchange' because 'deviceorientation' 
        // fires continuously, and we read current screenAngle inside the handler.

        return () => {
            if (supportsAbsolute) {
                window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
            } else {
                window.removeEventListener('deviceorientation', handleOrientation, true);
            }
        };
    }, []);

    return heading;
};
